import {faSparkles} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Button, Drawer, List} from 'antd';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useDomainConfig} from '../../../../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../../../../provider/domain-config/domain-config-type';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../../../../service/analytics/analytics';
import {useReviewAnalysisAiChat} from '../../../../../../service/reivew-analysis/review-analysis-ai-chat/review-analysis-ai-chat';
import {
    AiChatMessageRoleEnum,
    ConcordanceAiPresetEnum,
} from '../../../../../../service/reivew-analysis/review-analysis-ai-chat/review-analysis-ai-message-type';
import {
    ReviewConcordanceRequestFieldEnum,
    ReviewConcordanceRequestType,
} from '../../../../../../service/reivew-analysis/review-concordance/review-concordance-type';
import {useReviewsAnalysisFilter} from '../../../reviews-analysis-hook';
import {ConcordanceFormType} from '../concordance-form/concordance-form-type';

import {aiAnalysisPresetsTranslationKeys} from './ai-analysis-const';
import {AiChatInput} from './chat-input/ai-chat-input';
import {AiChatMessage} from './message/ai-chat-message';
import * as styles from './ai-analysis.scss';

type PropsType = {
    searchValues: ConcordanceFormType;
};

export function AiAnalysis(props: PropsType): JSX.Element | null {
    const {searchValues} = props;
    const [open, setOpen] = useState(false);
    const {domainName} = useDomainConfig();

    const {getLocalizedString} = useLocale();
    const {singleFilter} = useReviewsAnalysisFilter();

    const aiContextSearchParameters: Omit<ReviewConcordanceRequestType, 'page' | 'pageSize' | 'contextSize'> =
        useMemo(() => {
            const options = {
                startDate: searchValues[ReviewConcordanceRequestFieldEnum.Range][0],
                endDate: searchValues[ReviewConcordanceRequestFieldEnum.Range][1],
                [ReviewConcordanceRequestFieldEnum.Query]: searchValues[ReviewConcordanceRequestFieldEnum.Query],
                [ReviewConcordanceRequestFieldEnum.ContextSize]:
                    searchValues[ReviewConcordanceRequestFieldEnum.ContextSize],
            };

            return {
                ...singleFilter,
                ...options,
            };
        }, [searchValues, singleFilter]);

    const {messages, sendMessage, isTyping, answerText} = useReviewAnalysisAiChat(aiContextSearchParameters);

    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const hasTrackedFirstAiPrompt = useRef(false);

    // eslint-disable-next-line func-style
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSend = useCallback(() => {
        if (isTyping) {
            return;
        }

        if (!inputValue.trim()) {
            return;
        }

        const trimmedMessage = inputValue.trim();

        if (!hasTrackedFirstAiPrompt.current) {
            track(AnalyticsTarget.ReviewsAnalysis.AiChat, trimmedMessage);
            hasTrackedFirstAiPrompt.current = true;
        }

        sendMessage({
            message: trimmedMessage,
        });
        setInputValue('');
    }, [inputValue, isTyping, sendMessage]);

    const handleSendPreset = useCallback(
        (preset: ConcordanceAiPresetEnum) => {
            if (isTyping) {
                return;
            }

            const promptText = getLocalizedString(aiAnalysisPresetsTranslationKeys[preset]);

            if (!hasTrackedFirstAiPrompt.current) {
                track(AnalyticsTarget.ReviewsAnalysis.AiChat, promptText);
                hasTrackedFirstAiPrompt.current = true;
            }

            sendMessage({
                presetKey: preset,
                message: promptText,
            });
        },
        [getLocalizedString, isTyping, sendMessage]
    );

    const aiName = useMemo(() => {
        if (domainName === DomainNameEnum.brandWizard) {
            return 'WizardGPT';
        }

        if (domainName === DomainNameEnum.rocketData) {
            return 'RocketGPT';
        }

        return '';
    }, [domainName]);

    if (!aiName) {
        return null;
    }

    return (
        <>
            <Button
                className={styles.AiAnalysis_openButton}
                icon={<FontAwesomeIcon icon={faSparkles} />}
                onClick={() => setOpen(true)}
            >
                {aiName}
            </Button>
            <Drawer
                className={styles.AiAnalysis_drawer}
                footer={
                    <AiChatInput
                        disabled={isTyping}
                        inputValue={inputValue}
                        onSend={handleSend}
                        setInputValue={setInputValue}
                    />
                }
                onClose={() => setOpen(false)}
                open={open}
                placement="right"
                title={`✨ ${aiName}`}
                width={450}
            >
                <List
                    dataSource={messages}
                    renderItem={(item) => {
                        return (
                            <>
                                <List.Item className={styles.AiAnalysis_messageWrapper}>
                                    <AiChatMessage message={item.text} role={item.role} title={item.title} />
                                </List.Item>

                                {item.role === AiChatMessageRoleEnum.SYSTEM && (
                                    <List.Item className={styles.AiAnalysis_messageWrapper}>
                                        <div className={styles.AiAnalysis_presetsContainer}>
                                            <Text
                                                block
                                                secondary
                                                stringKey="REVIEWS_ANALYSIS__AI_ANALYSIS__PRESETS_TITLE"
                                            />
                                            {Object.values(ConcordanceAiPresetEnum).map((preset) => (
                                                <Button
                                                    className={styles.AiAnalysis_presetButton}
                                                    key={preset}
                                                    onClick={() => {
                                                        handleSendPreset(preset);
                                                    }}
                                                    size="small"
                                                >
                                                    <Locale stringKey={aiAnalysisPresetsTranslationKeys[preset]} />
                                                </Button>
                                            ))}
                                        </div>
                                    </List.Item>
                                )}
                            </>
                        );
                    }}
                />

                {isTyping && (
                    <AiChatMessage
                        message={
                            answerText ||
                            getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__TYPING', {
                                aiName,
                            })
                        }
                        role={AiChatMessageRoleEnum.ASSISTANT}
                        typing
                    />
                )}

                <div ref={messagesEndRef} />
            </Drawer>
        </>
    );
}
